import { combineEpics, ofType } from "redux-observable";
import { debounceTime, filter, map, switchMap } from "rxjs/operators";
import schedulerStorage from "../../dependencies/Storage";
import { reloadCalendar } from "../calendar";
import { createSelector } from "reselect";

import { fetchAvailableEmployees } from "../searchEmployees";

import { fetchItemTypes } from "../searchItemTypes";
import { FETCH_ITEM_TYPES_COMPLETE } from "../searchItemTypes";

/**
 * Returns a deep copy of the specified object.
 * @param {Object} value
 */
function deepCopy(value) {
  return JSON.parse(JSON.stringify(value));
}

/**
 * Sets a property of the specified object based on the selector and value.
 * @param {Object} object
 * @param {String} selector
 * @param {*} value
 */
function setProperty(object, selector, value) {
  const keys = selector.split(".");
  const container = keys
    .slice(0, keys.length - 1)
    .reduce((p, c) => p[c], object);
  container[keys[keys.length - 1]] = {
    ...(container[keys[keys.length - 1]] || {}),
    ...value,
  };
}

// Action types
export const LOAD_SAVED_SETTINGS = "LOAD_SAVED_SETTINGS";
const TOGGLE_DETAIL = "TOGGLE_DETAIL";
const TOGGLE_FINANCIAL = "TOGGLE_FINANCIAL";
const TOGGLE_LOCATION = "TOGGLE_LOCATION";
const TOGGLE_MATERIAL = "TOGGLE_MATERIAL";
const TOGGLE_PIN_TOTALS_ROW = "TOGGLE_PIN_TOTALS_ROW";
const UPDATE_FIELD = "UPDATE_FIELD";
export const UPDATE_TOTALS_ROW_FIELDS = "UPDATE_TOTALS_ROW_FIELDS";
export const UPDATE_SETTINGS = "UPDATE_SETTINGS";
const SET_ITEM_TYPES = "SET_ITEM_TYPES";

const FIELD_DEFAULTS = {
  general: {
    label: "General",
    fields: {
      jobName: {
        label: "Job Name",
        visible: true,
      },
      office: {
        label: "Office",
        visible: true,
      },
    },
  },
  details: {
    label: "Detail",
    fields: {
      description: {
        label: "Description",
        visible: true,
        onText: "ON",
        offText: "OFF",
      },
      jobStatus: {
        label: "Job Status",
        visible: true,
        onText: "ON",
        offText: "OFF",
      },
      customerName: {
        label: "Customer Name",
        visible: true,
        onText: "ON",
        offText: "OFF",
      },
      contact: {
        label: "Contact",
        visible: true,
        onText: "ON",
        offText: "OFF",
      },
      salesperson: {
        label: "Salesperson",
        visible: true,
      },
      deliveryStatus: {
        label: "Delivery Status",
        visible: true,
        onText: "ON",
        offText: "OFF",
      },
      supplier: {
        label: "Supplier",
        visible: true,
        onText: "ON",
        offText: "OFF",
      },
      deliveryJobs: {
        label: "Delivery Jobs",
        visible: true,
        onText: "ON",
        offText: "OFF",
      },
      priorityNotes: {
        label: "Priority Notes",
        visible: true,
        onText: "ON",
        offText: "OFF",
      },
    },
  },
  location: {
    label: "Location",
    fields: {
      line1: {
        label: "Line 1",
        visible: true,
      },
      line2: {
        label: "Line 2",
        visible: true,
      },
      city: {
        label: "City",
        visible: true,
      },
      state: {
        label: "State/Province/Region",
        visible: true,
      },
      zip: {
        label: "Postal Code",
        visible: true,
      },
      deliveryShipTo: {
        label: "Delivery Ship-To",
        visible: true,
      },
    },
  },
  financial: {
    label: "Financial",
    fields: {
      jobRevenue: {
        label: "Revenue",
        visible: true,
      },
      jobProposedProfit: {
        label: "Proposed Profit",
        visible: true,
      },
      jobActualProfit: {
        label: "Actual Profit",
        visible: true,
      },
      jobEstimatedLabor: {
        label: "Estimated Labor",
        visible: true,
      },
    },
  },
  material: {
    label: "Material",
    fields: {
      showEstimatedQuantities: {
        label: "Show Estimated Quantities",
        visible: false,
        onText: "ON",
        offText: "OFF",
      },
    },
  },
};

const DEFAULT_STATE = {
  bidTypes: [],
  color: "jobStatus",
  detailEnabled: true,
  entity: "jobs",
  schedulerView: "equipment",
  employeeSearchToggle: {
    togleState: false,
    toggleClass: "fa fa-chevron-right",
  },
  jobSearchToggle: {
    togleState: false,
    toggleClass: "fa fa-chevron-left",
  },
  fields: {},
  financialEnabled: false,
  jobStatuses: [],
  jobTypes: [],
  locationEnabled: false,
  materialEnabled: false,
  offices: [],
  salespeople: [],
  sortoption: "Date",
  SortTypes: [
    { label: "Date", value: "Date" },
    { label: "Customer", value: "Customer" },
    { label: "Address", value: "Address" },
  ],
  itemTypes: [],
  totalsRowFields: {
    fields: {
      showTotalsRow: {
        label: "Show Totals Row",
        visible: true,
        onText: "ON",
        offText: "OFF",
      },
      // Statistics and Included Item Types are sub-sections of the totals row
      statistics: {
        uomTotals: {
          label: "Show UOM Totals",
          visible: true,
          onText: "ON",
          offText: "OFF",
        },
        jobCounts: {
          label: "Show Job Counts",
          visible: true,
          onText: "ON",
          offText: "OFF",
        },
      },
      includedItemTypes: [],
    },
  },
  pinTotalsRow: false,
};

// Reducer
export default function (state = DEFAULT_STATE, { payload, type }) {
  switch (type) {
    case TOGGLE_DETAIL:
      return {
        ...state,
        detailEnabled: !state.detailEnabled,
      };
    case TOGGLE_FINANCIAL:
      return {
        ...state,
        financialEnabled: !state.financialEnabled,
      };
    case TOGGLE_LOCATION:
      return {
        ...state,
        locationEnabled: !state.locationEnabled,
      };
    case TOGGLE_MATERIAL:
      return {
        ...state,
        materialEnabled: !state.materialEnabled,
      };
    case TOGGLE_PIN_TOTALS_ROW:
      return {
        ...state,
        pinTotalsRow: !state.pinTotalsRow,
      };
    case UPDATE_FIELD:
      return {
        ...state,
        fields: {
          ...state.fields,
          [payload.key]: {
            ...(state.fields[payload.key] || {}),
            ...payload.options,
          },
        },
      };
    case UPDATE_SETTINGS:
      return {
        ...state,
        ...payload,
      };
    case SET_ITEM_TYPES:
      return {
        ...state,
        itemTypes: payload,
        totalsRowFields: {
          ...state.totalsRowFields,
          fields: {
            ...state.totalsRowFields.fields,
            includedItemTypes: {
              ...payload.reduce(
                (acc, itemType) => ({
                  ...acc,
                  [itemType.id]: itemType,
                }),
                {}
              ),
            },
          },
        },
      };
    default:
      return state;
    case UPDATE_TOTALS_ROW_FIELDS:
      return {
        ...state,
        totalsRowFields: {
          ...state.totalsRowFields,
          fields: {
            ...state.totalsRowFields.fields,
            [payload.key]:
              payload.key === "includedItemTypes"
                ? payload.options
                : {
                    ...(state.totalsRowFields.fields[payload.key] || {}),
                    ...payload.options,
                  },
          },
        },
      };
  }
}

// Action creators
export const loadSavedSettings = () => ({
  type: LOAD_SAVED_SETTINGS,
});

export const toggleDetail = () => ({
  type: TOGGLE_DETAIL,
});

export const toggleFinancial = () => ({
  type: TOGGLE_FINANCIAL,
});

export const toggleLocation = () => ({
  type: TOGGLE_LOCATION,
});

export const toggleMaterial = () => ({
  type: TOGGLE_MATERIAL,
});

export const togglePinTotalsRow = () => ({
  type: TOGGLE_PIN_TOTALS_ROW,
});

export const updateSettings = (values) => {
  return (dispatch) => {
    dispatch({ type: UPDATE_SETTINGS, payload: values });
    dispatch(fetchAvailableEmployees());
    dispatch(fetchItemTypes());
  };
};

export const updateField = (key, options) => ({
  type: UPDATE_FIELD,
  payload: {
    key,
    options,
  },
});

export const updateTotalsRowField = (key, options) => ({
  type: UPDATE_TOTALS_ROW_FIELDS,
  payload: {
    key,
    options,
  },
});

export const setItemTypes = (itemTypes) => ({
  type: SET_ITEM_TYPES,
  payload: itemTypes,
});

// Epics
const loadSavedSettingsEpic = (action$) =>
  action$.pipe(
    ofType(LOAD_SAVED_SETTINGS),
    switchMap(() => schedulerStorage.getItem("settings")),
    map((settings) => updateSettings(settings || {}))
  );

const updateSettingsEpic = (action$) =>
  action$.pipe(
    ofType(UPDATE_SETTINGS),
    map(() => reloadCalendar())
  );

export const handleItemTypesEpic = (action$) =>
  action$.pipe(
    ofType(FETCH_ITEM_TYPES_COMPLETE),
    map((action) => {
      return setItemTypes(action.payload);
    })
  );

const saveSettingsEpic = (action$, state$) =>
  action$.pipe(
    ofType(
      UPDATE_SETTINGS,
      TOGGLE_DETAIL,
      TOGGLE_FINANCIAL,
      TOGGLE_LOCATION,
      TOGGLE_MATERIAL,
      UPDATE_FIELD,
      SET_ITEM_TYPES,
      UPDATE_TOTALS_ROW_FIELDS
    ),
    debounceTime(1000),
    map(() => state$.value.settings),
    switchMap((settings) => schedulerStorage.setItem("settings", settings)),
    filter(() => false)
  );

export const epic = combineEpics(
  loadSavedSettingsEpic,
  updateSettingsEpic,
  saveSettingsEpic,
  handleItemTypesEpic
);

// Selectors
export const fieldsSelector = createSelector(
  (state) => state.settings.fields,
  (fields) => {
    return Object.keys(fields).reduce((prev, k) => {
      setProperty(prev, k, fields[k]);
      return prev;
    }, deepCopy(FIELD_DEFAULTS));
  }
);

export const itemTypesSelector = (state) => state.settings.itemTypes;
export const includedItemTypesSelector = (state) =>
  Object.values(state.settings.totalsRowFields.fields.includedItemTypes || {});
export const showEstimatedQuantitiesSelector = (state) => {
  // Check the raw fields storage first (dot-notation path)
  const rawField =
    state.settings.fields?.["material.fields.showEstimatedQuantities"];
  if (rawField !== undefined) {
    return rawField.visible || false;
  }
  // Fallback to nested path
  return (
    state.settings.fields?.material?.fields?.showEstimatedQuantities?.visible ||
    false
  );
};
